/**
 * The coach on every Growth tab (2026-10-28): up to three moves for this
 * week, written from that tab's own numbers (api lib/command/coach.ts).
 * Written on the first open each week; the refresh button writes them again.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui";
import { Card } from "@/features/command/ui";

export type CoachArea = "overview" | "ads" | "outreach" | "partners" | "affiliates" | "creators" | "social";

interface CoachMove { title: string; why: string; link?: string | null }
interface CoachNote { area: CoachArea; week_start: string; moves: CoachMove[]; by_ai: boolean; created_at: string }

export function CoachCard({ area, className }: { area: CoachArea; className?: string }) {
  const [note, setNote] = useState<CoachNote | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let alive = true;
    setNote(null);
    setFailed(false);
    api.get<{ note: CoachNote }>(`/command/coach/${area}`)
      .then((d) => { if (alive) setNote(d.note); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [area]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const d = await api.post<{ note: CoachNote }>(`/command/coach/${area}/refresh`);
      setNote(d.note);
      setFailed(false);
    } catch {
      /* keep what's there */
    } finally {
      setRefreshing(false);
    }
  }, [area]);

  return (
    <Card className={cn("relative overflow-hidden p-4 sm:p-5", className)}>
      <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="relative flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl cc-brand-bg shadow-lg shadow-indigo-500/20">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-300/90">
            This week's moves
            <button type="button" onClick={refresh} disabled={refreshing} title="Write them again from today's numbers" className="text-zinc-500 hover:text-zinc-200 disabled:opacity-60">
              <RefreshCw className={cn("h-3 w-3", refreshing && "animate-spin")} />
            </button>
          </div>
          {note ? (
            note.moves.length ? (
              <ol className="mt-2 space-y-2.5">
                {note.moves.map((m, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-[11px] font-semibold tabular-nums text-indigo-200">{i + 1}</span>
                    <div className="min-w-0">
                      {m.link ? (
                        <Link to={m.link} className="group inline-flex items-center gap-1 text-sm font-medium text-zinc-100 hover:text-indigo-200">
                          {m.title}
                          <ArrowRight className="h-3.5 w-3.5 opacity-60 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      ) : (
                        <div className="text-sm font-medium text-zinc-100">{m.title}</div>
                      )}
                      <p className="mt-0.5 text-[13px] leading-snug text-zinc-400">{m.why}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-1 text-sm text-zinc-400">Nothing to suggest yet. Moves show up once this tab has numbers to read.</p>
            )
          ) : failed ? (
            <p className="mt-1 text-sm text-zinc-400">Couldn't write this week's moves. Try the refresh button.</p>
          ) : (
            <div className="mt-2 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
